import React, { useEffect, useRef, useState } from 'react';
import './About.css';
import { AboutInfo, apiService } from '../services/api';
import PageLoader from './PageLoader';

const About: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [aboutInfo, setAboutInfo] = useState<AboutInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAboutInfo = async () => {
      try {
        const data = await apiService.getAbout();
        setAboutInfo(data);
      } catch (err) {
        console.error('Failed to fetch about info:', err);
        setAboutInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutInfo();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isMobile = () => window.innerWidth <= 768;

    const setScrollMode = (mobile: boolean) => {
      if (mobile) {
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';
      } else {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }
    };

    let currentIsMobile = isMobile();
    setScrollMode(currentIsMobile);

    if (currentIsMobile) {
      const handleResize = () => {
        const newIsMobile = isMobile();
        if (newIsMobile !== currentIsMobile) {
          currentIsMobile = newIsMobile;
          setScrollMode(currentIsMobile);

          if (!currentIsMobile) {
            window.location.reload();
          }
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        window.removeEventListener('resize', handleResize);
      };
    }

    let animationId: number;

    const handleScroll = (e: WheelEvent) => {
      e.preventDefault();

      if (animationId) {
        cancelAnimationFrame(animationId);
      }

      const delta = e.deltaY || e.deltaX;
      const scrollAmount = delta * 0.8;

      const newScrollLeft = Math.max(0,
        Math.min(container.scrollLeft + scrollAmount,
          container.scrollWidth - container.clientWidth)
      );

      animationId = requestAnimationFrame(() => {
        container.scrollLeft = newScrollLeft;
      });
    };

    let touchStartX = 0;
    let touchStartY = 0;
    let isTouchScrolling = false;
    let lastTouchX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      lastTouchX = touchStartX;
      isTouchScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const currentX = touch.clientX;
      const currentY = touch.clientY;

      if (!isTouchScrolling) {
        const deltaX = Math.abs(currentX - touchStartX);
        const deltaY = Math.abs(currentY - touchStartY);

        if (deltaX > deltaY && deltaX > 10) {
          isTouchScrolling = true;
        }
      }

      if (isTouchScrolling) {
        e.preventDefault();

        const deltaX = lastTouchX - currentX;
        const newScrollLeft = Math.max(0,
          Math.min(container.scrollLeft + deltaX,
            container.scrollWidth - container.clientWidth)
        );

        container.scrollLeft = newScrollLeft;
        lastTouchX = currentX;
      }
    };

    const handleTouchEnd = () => {
      isTouchScrolling = false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const scrollDistance = 400;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          container.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
          break;
        case 'ArrowRight':
          e.preventDefault();
          container.scrollBy({ left: scrollDistance, behavior: 'smooth' });
          break;
        case 'ArrowUp':
        case 'ArrowDown': {
          e.preventDefault();
          const direction = e.key === 'ArrowDown' ? 1 : -1;
          container.scrollBy({ left: scrollDistance * direction, behavior: 'smooth' });
          break;
        }
        case 'Work':
          e.preventDefault();
          container.scrollTo({ left: 0, behavior: 'smooth' });
          break;
        case 'End':
          e.preventDefault();
          container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
          break;
      }
    };

    const handleResize = () => {
      const newIsMobile = isMobile();
      if (newIsMobile !== currentIsMobile) {
        currentIsMobile = newIsMobile;
        setScrollMode(currentIsMobile);

        if (currentIsMobile) {
          window.location.reload();
        }
      }
    };

    container.addEventListener('wheel', handleScroll, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      container.removeEventListener('wheel', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  const renderContent = () => {
    const fallbackAvatar = '/images/ilyas.jpg';
 
    return (
      <>
        <p className="title">
          Artizim can help visualize even the craziest ideas
          <br />
          Converting them into elegant designs, awesome experiences and catchy brands.
        </p>
        <p className="subtitle">
          We breathe life into your company story, developing branding that advances your message and forms a distinct image.
          <br />
          While each phase moves your brand somewhere new and exciting, we continuously infuse direction from what we've learned about
          <br />
          Where the brand has come from, where you'd like the brand to go, and how the new brand image will impact consumer behavior.
          <br />
          At Artizim, we'll help you take the first step or conquer the latest
        </p>
        <img
          className="ilyas"
          src={aboutInfo?.avatar}
          alt={aboutInfo?.name}
          referrerPolicy="no-referrer"
          onError={(event) => {
            if (event.currentTarget.src.endsWith(fallbackAvatar)) return;
            event.currentTarget.src = fallbackAvatar;
          }}
        />
        
       {aboutInfo?.name?<p className="about-caption">{aboutInfo?.name}</p>:' '} 
      </>
    );
  };

  return (
    <div className="about">
      <div className="about-container" ref={containerRef}>
        {loading ? (
          <PageLoader label="Loading about" />
        ) : (
          <div className="about-content">
            <div style={{ display: 'flex', gap: '20px' }}>
              <div className="other page-content-enter">
                {renderContent()}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="scroll-indicator">
        <div className="scroll-hint">
          <span>← Swipe to browse sections →</span>
        </div>
      </div>
    </div>
  );
};

export default About;
